import {
  createLineItemReasonSchema,
  type LineItemReasonFormValues,
} from "@babackapp/return-widget-components/forms/line-item-reason.form";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useReturnReasons } from "@babackapp/return-widget-components/hooks/use-return-reasons";
import { useReturnableItems } from "@babackapp/return-widget-components/hooks/use-returnable-items";
import { useUpdateLineItem } from "@babackapp/return-widget-components/hooks/use-update-line-item";
import { useUploadImage } from "@babackapp/return-widget-components/mutations/use-upload-image";
import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useRef, useState } from "react";
import type { Resolver } from "react-hook-form";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useWidgetParams } from "./use-widget-params";

export function useLineItemReasonForm() {
  const navigate = useNavigate();
  const { lineItemId = "" } = useParams<{ lineItemId: string }>();
  const { orderId, shopId, shopInternalId, shopSlug } = useWidgetParams();
  const { data: shop } = usePublicShop(shopId);
  const updateLineItem = useUpdateLineItem(orderId);
  const uploadImage = useUploadImage(shopInternalId);
  const reasons = useReturnReasons(shopId);
  const draft = useDraftReturn(orderId);
  const returnableItems = useReturnableItems(orderId, shopSlug);

  const lineItem = returnableItems.find((li) => li.line_item_id === lineItemId);
  const draftItem = draft?.lineItems.find((li) => li.lineItemId === lineItemId);

  const returnableQty = Math.max(0, lineItem?.quantity_bought ?? 1);
  const [quantity, setQuantity] = useState(draftItem?.quantity ?? 1);

  const resolverRef = useRef<Resolver<LineItemReasonFormValues>>(
    zodResolver(
      createLineItemReasonSchema({
        requiresComment: false,
        requiresPhoto: false,
      }),
    ),
  );

  const stableResolver: Resolver<LineItemReasonFormValues> = useMemo(
    () => (values, context, options) =>
      resolverRef.current(values, context, options),
    [],
  );

  const form = useForm<LineItemReasonFormValues>({
    resolver: stableResolver,
    defaultValues: {
      reasonId: draftItem?.reasonId ?? reasons[0]?.id ?? "",
      comment: draftItem?.comment ?? null,
      images: [],
    },
  });

  const reasonId = useWatch({ control: form.control, name: "reasonId" });

  const selectedReason = useMemo(
    () => reasons.find((r) => r.id === reasonId) ?? null,
    [reasonId, reasons],
  );

  const schema = useMemo(
    () =>
      createLineItemReasonSchema({
        requiresComment: selectedReason?.requiresComment ?? false,
        requiresPhoto: selectedReason?.requiresPhoto ?? false,
      }),
    [selectedReason],
  );

  resolverRef.current = zodResolver(schema);

  const onSubmit = form.handleSubmit(async (values) => {
    const imageUrls = await Promise.all(
      values.images.map((file) => uploadImage.mutateAsync(file)),
    );

    updateLineItem(lineItemId, {
      quantity,
      reasonId: values.reasonId,
      comment: values.comment,
      imageUrls,
    });

    navigate("../resolution");
  });

  return {
    form,
    onSubmit,
    uploadImage,
    lineItemId,
    lineItem,
    reasons,
    selectedReason,
    quantity,
    setQuantity,
    returnableQty,
    userPortalTexts: shop?.preferences?.userPortal?.reasonStepInfo,
  };
}
